import React, { useState } from 'react';
import {
  CommunicationGuidanceDto,
  NeedDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { CommunicationType } from '@act/shared/types';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import {
  ChatCircleIcon,
  HandshakeIcon,
  HandHeartIcon,
  TargetIcon,
  ChatsIcon,
  CaretDownIcon,
  CaretRightIcon,
  PlusIcon,
  WarningIcon,
  ListChecksIcon,
  PencilLineIcon,
} from '@phosphor-icons/react';
import { SurveyDrawer } from './SurveyDrawer';
import { surveysSelectors } from 'redux/slices/surveys.slice';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import useAddActionDialog from '../hooks/use-add-action-dialog';
import { needIsExpiringSoon } from '@act/storybook';
import { useTracking } from 'react-components/tracking';
import NeedDetailSideDrawer from '../../needs/need-detail-dialog/NeedDetailSideDrawer';

export type UnifiedGoalsContentProps = {
  communicationGuidances: CommunicationGuidanceDto[];
  needs: NeedDto[];
  user: UserDto;
  isLoading?: boolean;
  error?: string | null;
  callEnded?: boolean;
};

export default function UnifiedGoalsContent({
  communicationGuidances,
  needs,
  user,
  isLoading = false,
  error = null,
  callEnded = false,
}: UnifiedGoalsContentProps) {
  const callDataQualityPreCallFF = useFeatureFlag('call-data-quality-pre-call');
  const needsCallFF = useFeatureFlag('needs-call');

  const hospitalizationSurvey =
    surveysSelectors.useSelectHospitalizationSurveyByMember({
      memberId: user?.id,
    });

  const hasOutstandingHospitalizationSurvey =
    hospitalizationSurvey && !hospitalizationSurvey?.completed;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [needDetailDialogOpen, setNeedDetailDialogOpen] = useState(false);
  const [selectedNeedId, setSelectedNeedId] = useState<number | null>(null);
  const openAddActionDialog = useAddActionDialog();
  const { generateTrackingProps } = useTracking();

  const handleOpenNeedDetail = (needId: number) => {
    setSelectedNeedId(needId);
    setNeedDetailDialogOpen(true);
  };

  const handleCloseNeedDetail = () => {
    setNeedDetailDialogOpen(false);
    // Delay clearing selectedNeedId to allow close drawer animation to complete
    setTimeout(() => {
      setSelectedNeedId(null);
    }, 300); // Match the close drawer animation duration
  };

  function handleGuidanceDisplay(guidance: CommunicationGuidanceDto): {
    icon: React.ReactNode;
    text: string;
    category: string;
  } {
    // Determine icon based on guidance category or communication type
    let icon = <ChatCircleIcon height={32} width={32} />;

    if (guidance.guidanceCategory === 'goals') {
      icon = <TargetIcon height={32} width={32} />;
    } else if (guidance.guidanceCategory === 'relationship') {
      icon = <HandshakeIcon height={32} width={32} />;
    } else if (guidance.guidanceCategory === 'needs') {
      icon = <HandHeartIcon height={32} width={32} />;
    }

    return {
      icon,
      text: guidance.copy,
      category: guidance.guidanceCategory || 'General',
    };
  }

  // If no feature flags are enabled and there's no outstanding hospitalization survey,
  // return null so that the goals content is not displayed.
  if (
    !callDataQualityPreCallFF &&
    !needsCallFF &&
    !hasOutstandingHospitalizationSurvey
  ) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
        <Typography variant="bodyMedium">Loading goals...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
        <Typography variant="bodyMedium" color="error">
          Failed to load goals
        </Typography>
      </Box>
    );
  }

  const filteredGuidances =
    communicationGuidances?.filter(
      guidance =>
        guidance?.communicationType?.includes(CommunicationType.CALL) ||
        guidance?.communicationType?.includes(CommunicationType.ALL)
    ) || [];

  const renderAllGoalItems = () => {
    // If we have a hospitalization survey, render ONLY that (highest priority, exclusive)
    if (hasOutstandingHospitalizationSurvey) {
      return (
        <Box
          key={`hospitalization-${hospitalizationSurvey.id}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '12px' : '16px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            height: 'auto',
            alignSelf: 'stretch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '8px' : '10px',
              height: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <ListChecksIcon size={32} />
            <Typography
              variant="bodyMedium"
              sx={{ fontWeight: 'bold', flex: 1 }}
            >
              Hospitalization follow-up
            </Typography>
            <Button
              variant="text"
              size="small"
              startIcon={<PencilLineIcon size={16} />}
              onClick={() => setIsDrawerOpen(true)}
              sx={{
                minWidth: 'auto',
                alignSelf: 'flex-start',
              }}
            >
              Complete now
            </Button>
          </Box>

          <SurveyDrawer
            survey={hospitalizationSurvey}
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
          />
        </Box>
      );
    }

    // If no hospitalization survey, show other goals
    const items: React.ReactNode[] = [];

    // Show needs (if feature flag is enabled and any exist)
    if (needsCallFF) {
      needs.forEach(need => {
        items.push(
          <Box
            key={`need-${need.id}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: isMobile ? '12px' : '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              height: 'auto',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '8px' : '10px',
                height: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '8px' : '10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    justifyContent: 'space-between',
                  }}
                >
                  <HandHeartIcon height={32} width={32} />
                  {needIsExpiringSoon(need) && (
                    <WarningIcon height={25} width={25} color="#ec9202" />
                  )}
                </Box>
                <Typography variant="bodyMedium" sx={{ fontWeight: 'bold' }}>
                  {need.issueLabel}
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  alignSelf: 'flex-start',
                  justifyContent: 'left',
                }}
                onClick={() => {
                  callEnded
                    ? openAddActionDialog({ need })
                    : handleOpenNeedDetail(need.id);
                }}
                startIcon={
                  callEnded ? <PlusIcon /> : <CaretRightIcon size={20} />
                }
                {...generateTrackingProps(
                  callEnded
                    ? 'add-need-action-from-call-button'
                    : 'view-need-from-call-button'
                )}
              >
                {callEnded ? 'Add Action' : 'View Need'}
              </Button>
            </Box>
          </Box>
        );
      });
    }

    // Show communication guidance (if feature flag is enabled)
    if (callDataQualityPreCallFF && filteredGuidances.length > 0) {
      filteredGuidances.forEach(guidance => {
        items.push(
          <Box
            key={`guidance-${guidance.id}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: isMobile ? '12px' : '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              height: 'auto',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '8px' : '10px',
                height: '100%',
                justifyContent: 'flex-start',
              }}
            >
              {handleGuidanceDisplay(guidance).icon}
              <Typography
                variant="bodyMedium"
                sx={{ fontWeight: 'bold', flex: 1 }}
              >
                {handleGuidanceDisplay(guidance).text}
              </Typography>
            </Box>
          </Box>
        );
      });
    }

    // If no items to show, display appropriate message
    if (items.length === 0) {
      return (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}
        >
          <Typography variant="bodyMedium">No goals available</Typography>
        </Box>
      );
    }

    return items;
  };

  return (
    <>
      {isMobile ? (
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<CaretDownIcon weight="bold" />}
            sx={{
              padding: '8px 0',
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
                alignItems: 'center',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <ChatsIcon width={32} height={32} />
              <Typography variant="titleMedium" sx={{ fontWeight: 'bold' }}>
                Goals For {user?.firstName}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {renderAllGoalItems()}
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              paddingBottom: '16px',
              alignItems: 'center',
            }}
          >
            <ChatsIcon width={32} height={32} />
            <Typography variant="titleMedium" sx={{ fontWeight: 'bold' }}>
              Goals For {user?.firstName}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '24px',
            }}
          >
            {renderAllGoalItems()}
          </Box>
        </Box>
      )}
      {selectedNeedId && needsCallFF && (
        <NeedDetailSideDrawer
          open={needDetailDialogOpen}
          onClose={handleCloseNeedDetail}
          needId={selectedNeedId}
        />
      )}
    </>
  );
}
